import {
  faMessage,
  faPaperPlaneTop,
  faTrash
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import z from 'zod'
import { useNewMessageMutation } from '~/api/mutations/action'
import { getApiError } from '~/api/problem'
import { useMessageSendingInfo } from '~/api/queries/global'
import { useMeInfos } from '~/api/queries/user'
import type { ApiResponseError } from '~/api/utils'
import {
  EditorInput,
  Form,
  InputCol,
  InputLine,
  Submit
} from '~/components/Form'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { forceSendRow } from '~/components/Scopes/Actions/Actions.css'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { DetailPanel } from '~/domains/actions/components/DetailPanel'
import { DetailToggle } from '~/domains/actions/components/DetailToggle'
import { RecipientList } from '~/domains/actions/components/RecipientList'
import { ActionErrorType } from '~/domains/actions/errors'
import { useSendConfirmationFlow } from '~/domains/actions/hooks/useSendConfirmationFlow'
import type { RecipientDisplayItem } from '~/domains/actions/types'
import { useApplicantsHead } from '~/domains/applicants/queries/applicantHead'
import type { ApplicantHeadData } from '~/domains/applicants/types'
import type { CompanyHead } from '~/domains/companies/types'
import {
  useAgencyConfig,
  useConfig
} from '~/domains/configuration/queries/config'
import { useContactsHead } from '~/domains/contacts/queries/useContactHead'
import type { ContactHeadData } from '~/domains/contacts/types'
import { AvailabilityToggle } from '~/domains/messaging/components/AvailabilityToggle'
import { SmsAlerts } from '~/domains/messaging/components/SmsAlerts'
import { SmsInfoBar } from '~/domains/messaging/components/SmsInfoBar'
import { SmsTemplateSelector } from '~/domains/messaging/components/SmsTemplateSelector'
import { SmsErrorType } from '~/domains/messaging/errors'
import { useSmsTemplates } from '~/domains/messaging/queries/useSmsTemplates'
import { useActiveAgency } from '~/domains/users/hooks/useActiveAgency'
import { toast, utf8ToGsm0338 } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { formatName } from '~/utils/names'
import { AsideActionsItems } from './AsideActionsItems'
import { useCloseAction } from './hooks/useCloseAction'

const formSchema = z.object({
  message: z.string({ message: 'Le message ne peut pas être vide' })
})

type FormValues = z.infer<typeof formSchema>

type ApplicantData = ApplicantHeadData & {
  phoneMobile: string | null
}

type ContactData = ContactHeadData & {
  company: CompanyHead
  phoneMobile: string | null
}

type MessageActionProps = {
  applicantIds?: number[]
  contactIds?: number[]
  removeRecipientIdFromList?: (applicantId: number) => void
  onClose?: () => void
  formId?: string
}

export const MessageAction = ({
  applicantIds,
  contactIds,
  removeRecipientIdFromList,
  onClose,
  formId
}: MessageActionProps) => {
  const queryClient = useQueryClient()
  const userInfo = useMeInfos()
  const { activeAgency, isLoading: isAgencyLoading } = useActiveAgency()
  const applicantDataHeadQueries = useApplicantsHead<ApplicantData>({
    applicantIds: applicantIds ?? [],
    additionalFields: ['phoneMobile']
  })
  const applicantDataHead = applicantDataHeadQueries
    .map((query) => query.data)
    .filter((data) => data != null)
  const contactDataHeadQueries = useContactsHead<ContactData>({
    contactIds: contactIds ?? [],
    additionalFields: ['company', 'phoneMobile']
  })
  const contactDataHead = contactDataHeadQueries
    .map((query) => query.data)
    .filter((data) => data != null)
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { data: availabilitySmsEnabled } = useConfig(
    'sms.template_availability_enabled'
  )
  const agencyId = activeAgency?.id ?? null
  const { data: agencyAvailabilityContent } = useAgencyConfig(
    'sms.template_availability_content',
    availabilitySmsEnabled && agencyId ? agencyId : undefined
  )

  const [message, setMessage] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [currentRecipientIds, setCurrentRecipientIds] = useState<number[]>(
    applicantIds ?? []
  )
  const [isAvailabilityRequest, setIsAvailabilityRequest] = useState(false)

  const {
    data: sendingInfo,
    isLoading: isLoadingSendingInfo,
    isError: isErrorSendingInfo
  } = useMessageSendingInfo({
    agencyId
  })
  const newMessageMutation = useNewMessageMutation()
  const { setIsDirty } = useContext(AsideActionContext)
  const closeAction = useCloseAction()

  const onActionClose = (e?: React.MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault()
    }
    closeAction().then((confirm) => {
      if (confirm) {
        onClose?.()
      }
    })
  }

  const isContactMode = (contactIds?.length ?? 0) > 0
  const recipientLabel = isContactMode ? 'contact' : 'candidat'
  const applicantRefusesMessaging =
    applicantDataHead.filter((data) => data.acceptsMessaging === false).length >
    0
  const contactRefusesMessaging =
    contactDataHead.filter((data) => data.acceptsMessaging === false).length > 0

  const {
    invalidRecipients,
    showInvalidDetail,
    setShowInvalidDetail,
    allRecipientsInvalid: allRecipientsInvalidPhone,
    setAllRecipientsInvalid: setAllRecipientsInvalidPhone,
    skippedAfterSendRecipients,
    showSkippedDetail,
    setShowSkippedDetail,
    resetBeforeSend,
    handleSendSuccess
  } = useSendConfirmationFlow({
    sentToastTitle: 'Message envoyé',
    sentToastMessage: 'Le message a bien été envoyé',
    onSendSuccess: () => {
      queryClient.cancelQueries({ queryKey: ['messageSendingInfo', agencyId] })
    },
    onFullySent: () => {
      closeAction(true)
      onClose?.()
    }
  })

  // Handle applicantIds/contactIds change from parent if new action is triggered
  useEffect(() => {
    if (applicantIds) {
      setCurrentRecipientIds(applicantIds)
      resetBeforeSend()
    }
    if (contactIds) {
      setCurrentRecipientIds(contactIds)
      resetBeforeSend()
    }
  }, [applicantIds, contactIds, resetBeforeSend])

  const handleSendError = (error: ApiResponseError) => {
    const problem = getApiError(error)
    if (problem?.type === SmsErrorType.AllRecipientsInvalidPhone) {
      const invalidApplicantIds = Array.isArray(
        problem.extensions.invalidApplicants
      )
        ? (problem.extensions.invalidApplicants as number[])
        : []
      const invalidContactIds = Array.isArray(
        problem.extensions.invalidContacts
      )
        ? (problem.extensions.invalidContacts as number[])
        : []
      setAllRecipientsInvalidPhone({
        applicantIds: invalidApplicantIds,
        contactIds: invalidContactIds
      })
      return
    }
    if (problem?.type === ActionErrorType.LimitExceeded) {
      const limit = problem.extensions.limit
      toast(
        'Limite dépassée',
        typeof limit === 'number'
          ? `Vous ne pouvez pas envoyer de message à plus de ${limit} ${recipientLabel}${limit > 1 ? 's' : ''} à la fois`
          : `Vous ne pouvez pas envoyer de message à autant de ${recipientLabel}s à la fois`,
        ToastType.Danger
      )
      return
    }
    if (error.httpStatus === 402) {
      toast(
        'Pas assez de crédit SMS',
        "Vous n'avez pas assez de crédit SMS pour envoyer ce message",
        ToastType.Danger
      )
      setError('Pas assez de crédit SMS')
      return
    }
    setError("Erreur lors de l'envoi du message")
    toast(
      'Une erreur est survenue',
      "Erreur lors de l'envoi du message",
      ToastType.Danger
    )
  }

  const onSubmit = async (values: FormValues) => {
    setError(null)
    resetBeforeSend()
    await newMessageMutation.mutateAsync(
      {
        ...(isContactMode
          ? { contacts: currentRecipientIds }
          : { applicants: currentRecipientIds }),
        message: values.message,
        availabilityRequest: isAvailabilityRequest
      },
      { onSuccess: handleSendSuccess, onError: handleSendError }
    )
  }

  const onForce = async () => {
    if (agencyId === null) return
    resetBeforeSend()
    await newMessageMutation.mutateAsync(
      {
        ...(isContactMode
          ? { contacts: currentRecipientIds }
          : { applicants: currentRecipientIds }),
        message,
        force: true,
        availabilityRequest: isAvailabilityRequest
      },
      { onSuccess: handleSendSuccess, onError: handleSendError }
    )
  }

  const { data: templatesData } = useSmsTemplates(
    isContactMode ? { forContact: true } : { forApplicant: true }
  )
  const templates = templatesData?.['hydra:member'] ?? []

  const templateGroups = (['instance', 'agency', 'user'] as const)
    .map((v) => ({
      visibility: v,
      items: templates.filter((t) => t.visibility === v)
    }))
    .filter((g) => g.items.length > 0)

  const warningMessage =
    "L'utilisation de certains caractères tels que â, ê, ô ou € multipliant par 2 le nombre de SMS envoyés, la saisie de ces caractères est bloquée."

  const getRecipientUrl = (id: number, company?: CompanyHead | null) => {
    if (isContactMode && company) {
      return `${legacyUrl}/admin/entreprises/edit/${company.id}#contacts/${id}`
    }
    return `${legacyUrl}/admin/candidats/edit/${id}`
  }

  const toDisplayItems = (
    ids: { applicantIds: number[]; contactIds: number[] } | null
  ): RecipientDisplayItem[] =>
    ids
      ? [
          ...ids.applicantIds.map((id): RecipientDisplayItem => {
            const data = applicantDataHead.find((a) => a.id === id)
            return {
              id,
              type: 'applicant',
              label: formatName(data) ?? `${recipientLabel} #${id}`,
              href: getRecipientUrl(id, null),
              secondaryText: data?.phoneMobile
                ? data.phoneMobile
                : 'Pas de numéro',
              removable: Boolean(data)
            }
          }),
          ...ids.contactIds.map((id): RecipientDisplayItem => {
            const data = contactDataHead.find((c) => c.id === id)
            return {
              id,
              type: 'contact',
              label: formatName(data) ?? `${recipientLabel} #${id}`,
              href: getRecipientUrl(id, data?.company ?? null),
              secondaryText: data?.phoneMobile
                ? data.phoneMobile
                : 'Pas de numéro',
              removable: Boolean(data)
            }
          })
        ]
      : []

  let placeholder = null

  if (isLoadingSendingInfo || !userInfo || isAgencyLoading) {
    placeholder = <Loader small={true} forceText={true} />
  }

  if (applicantDataHead.length === 1 && applicantRefusesMessaging) {
    placeholder = (
      <SmsAlerts
        acceptsMessaging={false}
        recipientLabel="candidat"
        onDismiss={onActionClose}
      />
    )
  }

  if (contactDataHead.length === 1 && contactRefusesMessaging) {
    placeholder = (
      <SmsAlerts
        acceptsMessaging={false}
        recipientLabel="contact"
        onDismiss={onActionClose}
      />
    )
  }

  if (isErrorSendingInfo) {
    placeholder = (
      <Alert
        message="Une erreur est survenue lors de la récupération des informations d'envoi de SMS."
        variation={AlertVariation.Danger}
        onDismiss={onActionClose}
      />
    )
  }

  if (activeAgency === null && !isAgencyLoading) {
    placeholder = <SmsAlerts onDismiss={onActionClose} />
  }

  if (sendingInfo && sendingInfo.service === null) {
    placeholder = (
      <SmsAlerts sendingInfo={sendingInfo} onDismiss={onActionClose} />
    )
  }

  if (skippedAfterSendRecipients) {
    placeholder = (
      <>
        <Alert
          message="Le message a bien été envoyé"
          variation={AlertVariation.Success}
          onDismiss={() => {
            closeAction(true)
            onClose?.()
          }}
        />
        <Alert
          message="Les destinataires sans numéro de téléphone valide n'ont pas reçu le message"
          variation={AlertVariation.Warning}
        >
          <DetailToggle
            isOpen={showSkippedDetail}
            onToggle={() => setShowSkippedDetail((v) => !v)}
          />
          <DetailPanel isOpen={showSkippedDetail}>
            <RecipientList
              recipients={toDisplayItems(skippedAfterSendRecipients)}
            />
          </DetailPanel>
        </Alert>
      </>
    )
  }

  if (placeholder) {
    return (
      <AsideAction
        title="Envoyer un SMS"
        icon={faMessage}
        applicantIds={isContactMode ? undefined : currentRecipientIds}
        contactIds={!isContactMode ? undefined : currentRecipientIds}
        onClose={onClose}
      >
        {placeholder}
      </AsideAction>
    )
  }

  if (sendingInfo && userInfo) {
    const invalidRecipientsForDisplay = toDisplayItems(invalidRecipients)
    const allRecipientsInvalidPhoneForDisplay = toDisplayItems(
      allRecipientsInvalidPhone
    )

    return (
      <Form
        id={formId}
        onSubmit={onSubmit}
        schema={formSchema}
        globalError={error}
        globalErrorInSubmit={true}
      >
        <AsideAction
          title="SMS"
          icon={faMessage}
          applicantIds={isContactMode ? undefined : currentRecipientIds}
          contactIds={!isContactMode ? undefined : currentRecipientIds}
          onClose={onClose}
        >
          <AsideActionsItems>
            <Button
              variation={ButtonVariation.DangerInversed}
              type="button"
              onClick={onActionClose}
              buttonSize="small"
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
            <Submit
              buttonVariation={ButtonVariation.Primary}
              submitAlignment={SubmitAlignment.Right}
              loadingText="Envoi..."
              successText={
                invalidRecipients || allRecipientsInvalidPhone
                  ? 'Envoyer'
                  : 'Envoi en cours'
              }
              onSuccessReset={false}
              buttonSize="small"
            >
              <FontAwesomeIcon icon={faPaperPlaneTop} />
              Envoyer
            </Submit>
          </AsideActionsItems>
          <InputCol>
            {!isContactMode && availabilitySmsEnabled && (
              <InputLine justify="start">
                <AvailabilityToggle
                  availabilityContent={agencyAvailabilityContent ?? ''}
                  onChange={(checked) => {
                    setIsAvailabilityRequest(checked)
                    setIsDirty(true)
                  }}
                />
              </InputLine>
            )}
            {templateGroups.length > 0 && (
              <SmsTemplateSelector
                templates={templates}
                agencyId={agencyId}
                onSelect={(content) => {
                  setMessage(content)
                  setIsDirty(true)
                }}
              />
            )}
            <EditorInput
              name="message"
              placeholder="Votre message"
              rows={5}
              required
              textOnly
              placeholderCategories={[
                'Identité',
                isContactMode ? 'Entreprise' : ''
              ]}
              showBottomMenu={false}
              content={message}
              onChange={(value) => {
                const sanitized = utf8ToGsm0338(value)
                if (sanitized !== value) {
                  toast(
                    'Caractères interdits',
                    warningMessage,
                    ToastType.Warning
                  )
                }
                setMessage(sanitized)
                setIsDirty(true)
              }}
              contextInfo={warningMessage}
            />
            <SmsInfoBar content={message} sendingInfo={sendingInfo} />
          </InputCol>
          {allRecipientsInvalidPhone && (
            <Alert
              variation={AlertVariation.Danger}
              message={`${allRecipientsInvalidPhoneForDisplay.length} ${recipientLabel}${allRecipientsInvalidPhoneForDisplay.length > 1 ? 's' : ''} n'${allRecipientsInvalidPhoneForDisplay.length > 1 ? 'ont' : 'a'} pas de numéro de téléphone valide, le message n'a pas pu être envoyé`}
              onDismiss={onActionClose}
            >
              <RecipientList
                recipients={allRecipientsInvalidPhoneForDisplay}
                canRemove={(currentRecipientIds?.length ?? 0) > 1}
                onRemove={
                  removeRecipientIdFromList
                    ? (id, type) => {
                        removeRecipientIdFromList(id)
                        setCurrentRecipientIds((prev) =>
                          prev.filter((recipientId) => recipientId !== id)
                        )
                        setAllRecipientsInvalidPhone((prev) =>
                          prev
                            ? type === 'contact'
                              ? {
                                  ...prev,
                                  contactIds: prev.contactIds.filter(
                                    (contactId) => contactId !== id
                                  )
                                }
                              : {
                                  ...prev,
                                  applicantIds: prev.applicantIds.filter(
                                    (applicantId) => applicantId !== id
                                  )
                                }
                            : prev
                        )
                      }
                    : undefined
                }
              />
            </Alert>
          )}
          {invalidRecipients && (
            <Alert
              variation={AlertVariation.Warning}
              message={`${invalidRecipientsForDisplay.length} ${recipientLabel}${invalidRecipientsForDisplay.length > 1 ? 's' : ''} n'${invalidRecipientsForDisplay.length > 1 ? 'ont' : 'a'} pas de numéro de téléphone mobile valide`}
            >
              <DetailToggle
                isOpen={showInvalidDetail}
                onToggle={() => setShowInvalidDetail((v) => !v)}
              />
              <DetailPanel isOpen={showInvalidDetail}>
                <RecipientList recipients={invalidRecipientsForDisplay} />
              </DetailPanel>
              <div className={forceSendRow}>
                <InputLine justify="end">
                  <Button
                    variation={ButtonVariation.Primary}
                    type="button"
                    onClick={onForce}
                    buttonSize="small"
                    disabled={newMessageMutation.isPending}
                  >
                    <FontAwesomeIcon icon={faPaperPlaneTop} />
                    {newMessageMutation.isPending
                      ? 'Envoi...'
                      : "Poursuivre l'envoi"}
                  </Button>
                </InputLine>
              </div>
            </Alert>
          )}
          {applicantRefusesMessaging && (
            <Alert
              variation={AlertVariation.Warning}
              message={
                'Certains candidats sélectionnés ne souhaitent pas être contactés par SMS'
              }
              onDismiss={onActionClose}
            />
          )}
          {contactRefusesMessaging && (
            <Alert
              variation={AlertVariation.Warning}
              message={
                'Certains contacts sélectionnés ne souhaitent pas être contactés par SMS'
              }
              onDismiss={onActionClose}
            />
          )}
        </AsideAction>
      </Form>
    )
  }
}
